/** ExperimentTabWidget — test draft config/prompt versions before publishing.
 * Replaces components/settings/agent/ExperimentTab.tsx.
 *
 * Fetches draft versions via useDraftVersions, renders readiness status,
 * embeds TestChatPanelWidget, and provides Publish / Discard actions.
 */

import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { toast } from '@/shared/ui/sonner';

import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/lib/cn';
import { useSkillsQuery } from '@/entities/skill';
import { useDraftVersions } from '../../../features/settings/draft-preview/useDraftVersions';
import { useConfirmedAction } from '../../../components/settings/agent/useConfirmedAction';
import { log } from '@/shared/logger';
import PublishConfirmDialog from '../../../components/settings/agent/PublishConfirmDialog';
import TestChatPanelWidget from './TestChatPanelWidget';
import EvalTestSetWidget from './EvalTestSetWidget';
import EvalConfigPanel from './EvalConfigPanel';
import EvalRunPanel from './EvalRunPanel';
import { useEvalConfig } from '../../../features/settings/evaluation/useEvalConfig';
import { useTestCases } from '../../../features/settings/evaluation/useTestCases';

export default function ExperimentTabWidget() {
  const { agentId = '' } = useParams<{ agentId: string }>();
  const { activeConfig, activePrompt, draftConfig, draftPrompt, loading, error, reload, publishDrafts, discardDrafts } =
    useDraftVersions(agentId);
  const { data: skillsData = [] } = useSkillsQuery();
  const evalConfig = useEvalConfig(agentId);
  const testCases = useTestCases(agentId);
  const skills = skillsData ?? [];
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);

  const hasDraftVersion = draftConfig !== null || draftPrompt !== null;
  const hasSkillSelection = selectedSkillIds.length > 0;
  const isReady = hasDraftVersion || hasSkillSelection;

  const versionLabel = hasDraftVersion
    ? [
    draftPrompt ? `prompt v${draftPrompt.versionNumber}` : null,
    draftConfig ? `config v${draftConfig.versionNumber}` : null,
    ].filter(Boolean).join(' + ') || 'active'
    : `${selectedSkillIds.length} skill${selectedSkillIds.length === 1 ? '' : 's'}`;

  const publish = useConfirmedAction(
    async () => {
      await publishDrafts();
      toast.success('Published successfully');
    },
    (err) => log.error('agent_publish_failed', err),
  );

  const discard = useConfirmedAction(
    async () => {
      await discardDrafts();
      toast.info('Drafts discarded');
    },
    (err) => log.error('agent_discard_failed', err),
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-4 pt-4">
        <p className="text-sm">Loading experiment status...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pt-4">
      {error && <p className="p-3 text-sm text-destructive">{error}</p>}

      <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
        <span
          className={cn(
            'rounded px-2 py-0.5 text-xs font-bold',
            isReady
              ? 'bg-status-success-subtle text-status-success'
              : 'bg-muted text-muted-foreground',
          )}
        >
          {isReady ? 'Ready' : 'Not Ready'}
        </span>
        <p className="text-sm text-foreground">
          {isReady
            ? `Testing: ${versionLabel}`
            : 'Save a draft or select one or more skills to begin experimenting.'}
        </p>
      </div>

      {skills.length > 0 && (
        <section className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3" aria-label="Selected skills">
          <h3 className="text-sm font-semibold text-foreground">Skills</h3>
          <ul className="flex flex-col gap-2">
            {skills.map((skill) => {
              const checked = selectedSkillIds.includes(skill.id);
              return (
                <li key={skill.id} className="flex items-center gap-2">
                  <input
                    id={`skill-${skill.id}`}
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => {
                      setSelectedSkillIds((current: string[]) =>
                        event.target.checked
                          ? [...current, skill.id]
                          : current.filter((id: string) => id !== skill.id),
                      );
                    }}
                  />
                  <label htmlFor={`skill-${skill.id}`} className="text-sm text-foreground">
                    {skill.name}
                  </label>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {isReady ? (
        <>
          {hasDraftVersion && (
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="destructive"
                onClick={discard.trigger}
                disabled={discard.pending || publish.pending}
              >
                Discard Drafts
              </Button>
              <Button
                onClick={publish.trigger}
                disabled={publish.pending || discard.pending}
              >
                Publish
              </Button>
            </div>
          )}

          <TestChatPanelWidget
            agentId={agentId}
            promptVersionId={draftPrompt?.id ?? null}
            configVersionId={draftConfig?.id ?? null}
            draftSkillIds={hasSkillSelection ? selectedSkillIds : null}
            versionLabel={versionLabel}
          />
        </>
      ) : (
        <div className="flex flex-col items-center gap-2 p-8 text-center text-muted-foreground">
          <p className="text-base font-semibold text-foreground">Nothing to test yet</p>
          <p className="text-sm">
            Go to the Config or Prompt tab and save a draft to begin experimenting.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-6 border-t border-border pt-4">
        <EvalConfigPanel state={evalConfig} />
        <EvalTestSetWidget state={testCases} />
        <EvalRunPanel
          agentId={agentId}
          activeConfig={activeConfig}
          activePrompt={activePrompt}
          draftConfig={draftConfig}
          draftPrompt={draftPrompt}
          testCaseCount={testCases.testCases.length}
          passThreshold={evalConfig.config?.pass_threshold ?? null}
          onDraftSaved={() => void reload()}
        />
      </div>

      <PublishConfirmDialog
        isOpen={publish.showConfirm}
        onConfirm={publish.confirm}
        onCancel={publish.cancel}
        title="Publish drafts?"
      />
      <PublishConfirmDialog
        isOpen={discard.showConfirm}
        onConfirm={discard.confirm}
        onCancel={discard.cancel}
        title="Discard all drafts?"
      />
    </div>
  );
}
